import React, { useState, useRef, useCallback } from 'react';
import './App.css';
import { WaveAnimation } from './components/WaveAnimation';
import { BirdSilhouette } from './components/BirdSilhouette';
import { Fireflies } from './components/Fireflies';
import { Play, Copy, Check, ExternalLink, Volume2, VolumeX } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_bb278d88-590e-4abf-982e-467de245f474/artifacts/8962gqwn_LSPA%20greengold.png';
const VIDEO_TEASER = '/lspa-teaser.mp4';
const VIDEO_GOLIVE = '/lspa-golive.mp4';
const CONTRACT_ADDRESS = '0x29845826AEF8E421a3924DB26b626DB156Cf54a1';
const ALLINX_URL = 'https://www.allinx.io/nexus';

function VideoPlayer({ src, label, testId }) {
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(true);
  const ref = useRef(null);

  const handleReplay = useCallback(() => {
    setEnded(false);
    if (ref.current) {
      ref.current.currentTime = 0;
      ref.current.play();
    }
  }, []);

  const toggleMute = useCallback(() => {
    setMuted(prev => {
      if (ref.current) {
        ref.current.muted = !prev;
      }
      return !prev;
    });
  }, []);

  return (
    <div className="video-card" data-testid={testId}>
      <div className="video-title-row">
        {label && <span className="video-label">{label}</span>}
        {!ended && (
          <button
            className={`sound-toggle-bar ${muted ? 'is-muted' : 'is-unmuted'}`}
            onClick={toggleMute}
            title={muted ? 'Ton einschalten' : 'Ton ausschalten'}
            data-testid={`${testId}-sound-toggle`}
          >
            {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
            <span>{muted ? 'Sound an' : 'Sound aus'}</span>
          </button>
        )}
      </div>
      <div className="video-wrapper">
        {ended ? (
          <div className="video-ended-overlay">
            <img src={LOGO_URL} alt="LSPA Token Logo" className="ended-logo" />
            <button className="replay-btn" onClick={handleReplay} data-testid={`${testId}-replay`}>
              <Play size={16} />
              <span>Nochmal abspielen</span>
            </button>
          </div>
        ) : (
          <video
            ref={ref}
            className="hero-video"
            autoPlay
            muted={muted}
            playsInline
            onEnded={() => setEnded(true)}
          >
            <source src={src} type="video/mp4" />
          </video>
        )}
      </div>
    </div>
  );
}

function App() {
  const [copied, setCopied] = useState(false);

  const copyAddress = useCallback(() => {
    navigator.clipboard.writeText(CONTRACT_ADDRESS).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  return (
    <div className="page-wrapper">
      {/* Background */}
      <div
        className="bg-photo-layer"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_token-lspa-info/artifacts/gc52qx2d_003.jpg')`,
        }}
      />
      <div className="bg-vignette" />
      <div className="bg-sea-glow" />
      <div className="bg-noise" />

      {/* Ambient */}
      <BirdSilhouette className="bird bird-1" />
      <BirdSilhouette className="bird bird-2" />
      <Fireflies />
      <div className="sand-ground" />
      <WaveAnimation />

      {/* Main content */}
      <div className="page-content">
        {/* Hero section */}
        <header className="hero-header">
          <img
            src={LOGO_URL}
            alt="LSPA Token Logo"
            className="hero-logo"
            data-testid="lspa-logo"
          />
          <div className="hero-text">
            <span className="live-badge" data-testid="live-badge">
              <span className="live-dot" />
              LIVE
            </span>
            <h1 className="hero-heading" data-testid="hero-heading">
              LSPA Token
            </h1>
            <p className="hero-subtitle">Legend Spa Hotel &middot; Legends Never Die</p>
          </div>
        </header>

        {/* Token Info Card */}
        <section className="info-card" data-testid="token-info-card">
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Blockchain</span>
              <span className="info-value" data-testid="blockchain-info">
                <span className="bsc-icon">BSC</span>
                Binance Smart Chain
              </span>
            </div>
            <div className="info-item contract-row">
              <span className="info-label">Contract Address</span>
              <div className="contract-value" data-testid="contract-address">
                <code className="contract-code">{CONTRACT_ADDRESS}</code>
                <button
                  className="copy-btn"
                  onClick={copyAddress}
                  title="Adresse kopieren"
                  data-testid="copy-address-btn"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>
            <div className="info-item">
              <span className="info-label">Jetzt verfügbar auf</span>
              <a
                href={ALLINX_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="exchange-link"
                data-testid="exchange-link"
              >
                AllinX Exchange – Nexus
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </section>

        {/* Videos Section */}
        <section className="videos-section" data-testid="videos-section">
          <div className="videos-stack">
            <VideoPlayer src={VIDEO_GOLIVE} label="Go Live" testId="video-golive" />
            <VideoPlayer src={VIDEO_TEASER} label="Token Teaser" testId="video-teaser" />
          </div>
        </section>
      </div>
    </div>
  );
}

export default App;
